import ConnectLoadable from 'components/ConnectLoadable';
import viewerUserPreferences from 'models/user/ViewerUserPreferences';
import viewerSocialProfile from 'models/user/ViewerSocialProfile';
import viewerUserBiometrics from 'models/user/ViewerUserBiometrics';
import ReactComponentPageView from 'views/main/react/ReactComponentPageView';
import DateFormatter from 'utils/date/DateFormatter';
import NavigationUtil from 'utils/NavigationUtil';
import UrlParser from 'utils/parsers/UrlParser';
import moment from 'moment';
import { REGEX_ISO_DATE_FORMAT } from 'utils/Regexes';
import { TABS } from 'Weight/WeightUtils';
import AsyncFeatureFlag from 'utils/AsyncPreference';
import GdprUploadUtil from '../backbone/utils/gdpr/GdprUploadUtil';
import {
    garminTrailsRoleKey, // Cleanup ticket: CA-159335
    dswRoleKey, // Clean up ticket: CA-161362
    reactWorkoutPageRoleKey, // Cleanup ticket: CA-167480,
    strengthTrainingPlansRoleKey, //Cleanup ticket: CA-174612
    newTrainingPlansPageRoleKey, // Clean up ticket: CA-181375
    respirationRoleKey, //Cleanup ticket: CA-185204
    reactReportPageRoleKey //Cleanup ticket: CA-183087
} from 'backbone/models/preferences/PreferencesMapping';
import { ecgRoute, jetLagRoute, bodyBatteryRoute, garminTrailsRoute } from './obfuscatedRoutes';
import AnalyticsUtil from '../backbone/utils/AnalyticsUtil';
import {
    blockInReadOnly,
    isRaceManagementMode,
    blockInAccountSharingReadonly,
    isAccountSharingReadonlyMode
} from 'system/SystemUtil';
import { GROUP_LIST_PAGE_TAB, TAB_IDS, groupRequestErrors } from 'utils/groups/GroupsUtil';
import { WorkoutPageState, SportTypes } from '../WorkoutPage/constants/WorkoutConstants';
import { isCDCUser } from 'utils/ChinaUtil';
import { BPPageRoute } from 'BloodPressure/BloodPressureConstant';
import {
    projectedRaceTimePredictionRoleKey, // Cleanup ticket: CA-192406
    showTrailsPageForUnauthenticatedUserKey,
    STPPhase2RoleKey
} from '../backbone/models/preferences/PreferencesMapping'; // Clean up ticket: CA-172718
import {
    GarminTrailDetailsHash,
    GarminTrailsPageHash,
    myTrailReviewsHash,
    subscription
} from '../services/omt/subscription/subscriptionFields';

import { navigateToSubscriptionPage } from 'GCP/utils/GCPNavigationUtil';

Window.App.MATSupportPage = function() {
    Window.App.displayPage(
        new ReactComponentPageView({
            userPreferences: viewerUserPreferences,
            page: ConnectLoadable(() => import('MATSupport/MATSupportPage'))
        })
    );
};

Window.App.ProfilePage = function(displayName) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();

    // In Account Sharing, user shouldn't see other people's profile page
    if (isAccountSharingReadonlyMode() && displayName && displayName !== viewerSocialProfileJson.displayName) {
        NavigationUtil.displayNoPrivilegesPage();
        return;
    }

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: displayName || viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            userPreferences: viewerUserPreferences,
            isUserLoggedIn: viewerSocialProfile.isUserLoggedIn(),
            page: ConnectLoadable(() => import('Profile/ProfilePage'))
        })
    );
};

Window.App.importData = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            userPreferences: viewerUserPreferences,
            isUserLoggedIn: viewerSocialProfile.isUserLoggedIn(),
            page: ConnectLoadable(() => import('ImportData/ImportDataPageView'))
        })
    );

    // displayPage() clears all modals, perform this step after so the GDPR modal does not get removed
    GdprUploadUtil.handleNoConsentGranted(true);
};

Window.App.NewsFeedPage = async function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('NewsFeed/NewsFeedPage'))
        })
    );
};

Window.App.CalendarPage = async function(year, month, queryString) {
    let action = NavigationUtil.getQueryParameterByName('action');
    let eventId = NavigationUtil.getQueryParameterByName('eventId');
    let hasGoalTime = NavigationUtil.getQueryParameterByName('hasGoalTime');
    let shareable = NavigationUtil.getQueryParameterByName('shareable') === 'true';
    let sharedEvent = NavigationUtil.getQueryParameterByName('sharedEvent');
    let shareableEventUuid = NavigationUtil.getQueryParameterByName('shareableEventUuid');
    let eventEdit = NavigationUtil.getQueryParameterByName('eventEdit') === 'true';
    let fromSearch = NavigationUtil.getQueryParameterByName('fromSearch') === 'true';
    let scheduledWorkoutId = NavigationUtil.getQueryParameterByName('scheduledWorkoutId');
    let workoutName = NavigationUtil.getQueryParameterByName('workoutName');

    if (
        action ||
        (eventId && hasGoalTime) ||
        eventEdit ||
        sharedEvent ||
        shareableEventUuid ||
        scheduledWorkoutId ||
        workoutName
    ) {
        NavigationUtil.clearQueryParameters();
        queryString = null;
    }

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerUserPreferences: viewerUserPreferences.toJSON(),
            viewerUserBiometrics: viewerUserBiometrics.toJSON(),
            page: ConnectLoadable(() => import('Calendar/CalendarPage')),
            year: year,
            month: month,
            code: queryString,
            addEvent: !!action && action === 'add-event',
            eventId: eventId,
            hasGoalTime: hasGoalTime,
            shareable: shareable,
            shareableEventUuid: shareableEventUuid,
            eventEdit: eventEdit,
            fromSearch: fromSearch,
            viewerSocialProfile: viewerSocialProfile.toJSON(),
            scheduledWorkoutId: scheduledWorkoutId,
            workoutName: workoutName
        })
    );
};

Window.App.CreateSegmentFromActivity = function(activityId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerUserPreferences: viewerUserPreferences.toJSON(),
            viewerUserBiometrics: viewerUserBiometrics.toJSON(),
            viewerSocialProfileJson: viewerSocialProfileJson,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('Segment/CreateSegmentFromActivityPage')),
            activityId: activityId,
            displayName: viewerSocialProfileJson.displayName
        })
    );
};

Window.App.SegmentsPage = function(uuid) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfileJson: viewerSocialProfileJson,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('Segment/SegmentsPage')),
            isUserLoggedIn: viewerSocialProfile.isUserLoggedIn(),
            displayName: viewerSocialProfileJson.displayName,
            segmentUuid: uuid
        })
    );
};
Window.App.SegmentsMainPage = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfileJson: viewerSocialProfileJson,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('Segment/SegmentsPage')),
            isUserLoggedIn: viewerSocialProfile.isUserLoggedIn(),
            displayName: viewerSocialProfileJson.displayName
        })
    );
};

Window.App.DivingPage = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('Diving/DivingPage')),
            isUserLoggedIn: viewerSocialProfile.isUserLoggedIn()
        })
    );
};

Window.App.ProgressSummaryPage = async function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            userPreferences: viewerUserPreferences,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('Reports/ProgressSummaryPage'))
        })
    );
};

Window.App.ReportsRangePage = async function(reportId, reportType, activeTab, startDate, untilDate) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const useReactPage = await AsyncFeatureFlag.isFeatureEnabled(reactReportPageRoleKey); //Cleanup ticket: CA-183087

    if (useReactPage) {
        Window.App.displayPage(
            new ReactComponentPageView({
                viewerUserPreferences: viewerUserPreferences.toJSON(),
                viewerSocialProfile: viewerSocialProfileJson,
                displayName: viewerSocialProfileJson.displayName,
                urlReportId: reportId,
                urlReportType: reportType,
                urlActiveTab: activeTab,
                urlStartDate: startDate,
                urlEndDate: untilDate,
                page: ConnectLoadable(() => import('ReportsPage/ReportsPage'))
            })
        );
    } else {
        // Backbone page
        import('pages/report/ReportController').then(({ default: ReportPageView }) => {
            const reportPageView = new ReportPageView({
                reportId: reportId,
                reportType: reportType,
                userPreferences: viewerUserPreferences,
                activeTab: activeTab,
                displayName: viewerSocialProfileJson.displayName,
                startDate: startDate,
                untilDate: untilDate
            });

            Window.App.displayPage(reportPageView);
        });
    }
};

Window.App.ReportsPage = async function(reportId, reportType, activeTab, vo2MaxToggle) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const useReactPage = await AsyncFeatureFlag.isFeatureEnabled(reactReportPageRoleKey); //Cleanup ticket: CA-183087

    if (useReactPage) {
        Window.App.displayPage(
            new ReactComponentPageView({
                viewerUserPreferences: viewerUserPreferences.toJSON(),
                viewerSocialProfile: viewerSocialProfileJson,
                displayName: viewerSocialProfileJson.displayName,
                vo2MaxToggle,
                urlReportId: reportId,
                urlReportType: reportType,
                urlActiveTab: activeTab,
                page: ConnectLoadable(() => import('ReportsPage/ReportsPage'))
            })
        );
    } else {
        // Backbone page
        import('pages/report/ReportController').then(({ default: ReportPageView }) => {
            let reportPageView = new ReportPageView({
                reportId: reportId,
                reportType: reportType,
                userPreferences: viewerUserPreferences,
                activeTab: activeTab,
                vo2MaxToggle: vo2MaxToggle,
                displayName: viewerSocialProfileJson.displayName
            });

            Window.App.displayPage(reportPageView);
        });
    }
};

Window.App.DelegationAuthorizationPage = async function() {
    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('AccountSharing/DelegationAuthorizationPage'))
        })
    );
};

Window.App.PopularityPage = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('Popularity/PopularityPage'))
        })
    );
};

Window.App.WorkoutsPage = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            fullLocale: viewerUserPreferences.getFullLocale(),
            page: ConnectLoadable(() => import('Workouts/WorkoutsPage'))
        })
    );
};

Window.App.BenchmarkExercisesPage = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            fullLocale: viewerUserPreferences.getFullLocale(),
            page: ConnectLoadable(() => import('Workouts/BenchmarkExercisesPage'))
        })
    );
};

Window.App.BenchmarkExerciseDetailsContainer = function(exerciseKey) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            fullLocale: viewerUserPreferences.getFullLocale(),
            page: ConnectLoadable(() => import('Workouts/BenchmarkExerciseDetailsContainer')),
            exerciseKey
        })
    );
};

Window.App.ExercisesPage = function(categoryKey, exerciseKey, queryString) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('Exercises/ExercisesPage')),
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            fullLocale: viewerUserPreferences.getFullLocale(),
            categoryKey,
            exerciseKey,
            queryString: queryString
        })
    );
};

Window.App.BadgesPage = function(displayName) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('Badges/BadgesPage')),
            displayName: displayName || viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson
        })
    );
};

Window.App.EditSegment = function(segmentUuid) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerUserPreferences: viewerUserPreferences.toJSON(),
            viewerUserBiometrics: viewerUserBiometrics.toJSON(),
            viewerSocialProfileJson: viewerSocialProfileJson,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('Segment/CreateSegmentFromActivityPage')),
            segmentUuid: segmentUuid,
            displayName: viewerSocialProfileJson.displayName
        })
    );
};

Window.App.ATPDailyPage = function(athletePlanId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const openWorkoutModal = NavigationUtil.getQueryParameterByName('openWorkoutModal');

    if (openWorkoutModal) {
        NavigationUtil.clearQueryParameters();
    }

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerUserPreferences: viewerUserPreferences.toJSON(),
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('AdaptiveTrainingPlans/ATPDailyPage')),
            athletePlanId: athletePlanId,
            openWorkoutModal: openWorkoutModal
        })
    );
};

Window.App.ATPArticlePage = function(athletePlanId, articleId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerUserPreferences: viewerUserPreferences.toJSON(),
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('AdaptiveTrainingPlans/ATPArticlePage')),
            articleId: articleId,
            athletePlanId: athletePlanId
        })
    );
};

Window.App.GolfStatsPage = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            profileImageUrlSmall: viewerSocialProfile.getProfileImageUrlSmall(),
            page: ConnectLoadable(() => import('GolfStats/containers/GolfStatsPageContainer'))
        })
    );
};

Window.App.GoToUsersHomePage = function() {
    //when Window.App.dashboard is called if the user has daily summary as home page it redirects to that page -
    //if not it shows the dashboard but does not change the url - so we remove manage-permissions as if it would be on the dashboard
    let url = window.location.href.substring(0, window.location.href.lastIndexOf('/'));
    NavigationUtil.replaceNavigationURL(url);

    Window.App.dashboard();
};

/*
 * params:
 * * date - usually a ISO date, could be the card to jump to on current day(all other params null) DS page or the display name(all params defined)
 * * card - usually the card, could be the date if date is display name
 * * oldRoute - usually null, could be the card to jump to.
 */
Window.App.DailySummaryPage = function(date, card, oldRoute) {
    // setup vars
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    let displayName = viewerSocialProfileJson.displayName;
    const todaysDate = DateFormatter.formatISODate(new Date());
    const route = '/daily-summary/';

    // if no date, there isn't any of the params, just go to viewer's current day
    if (!date) {
        date = todaysDate;
        NavigationUtil.navigate(`${route}${date}`, null, { replace: true });
    } else if (oldRoute) {
        // if there are 3 params, it's the old style(display/date/tab)
        let updatedUrl = `${card}/${oldRoute}`;

        if (displayName !== date) {
            displayName = date;
            updatedUrl = displayName + '/' + updatedUrl;
        }

        date = card;
        card = oldRoute;
        NavigationUtil.navigate(`${route}${updatedUrl}`, null, { replace: true });
    } else if (card && /\d{4}-\d{2}-\d{2}/.test(card)) {
        // if card is a date, it's an old url (display/date)
        let updatedUrl = card;
        if (date !== displayName) {
            displayName = date;
            updatedUrl = displayName + '/' + updatedUrl;
        }
        NavigationUtil.navigate(`${route}${updatedUrl}`, null, { replace: true });
    } else if (!card && !/\d{4}-\d{2}-\d{2}/.test(date)) {
        // if there isn't a card and date isn't a date, the url is (/card)
        // add today's date to url
        card = date;
        date = todaysDate;
        NavigationUtil.navigate(`${route}${date}/${card}`, null, { replace: true });
    } // if not caught before, it's either /date or /date/card, no changes needed

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('DailySummary/DailySummaryPage')),
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerSocialProfile: viewerSocialProfileJson,
            displayName: displayName,
            date: date,
            card: card,
            route: route
        })
    );
};

Window.App.MenstrualCycleTrackingPage = function(date) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON(),
        todayDate = DateFormatter.formatISODate(new Date());
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const route = '/menstrual-cycle-tracking/';

    // Disallow future dates in the URL
    if (date > todayDate) {
        NavigationUtil.navigate(route + todayDate);
    } else {
        Window.App.displayPage(
            new ReactComponentPageView({
                displayName: viewerSocialProfileJson.displayName,
                viewerSocialProfile: viewerSocialProfileJson,
                viewerUserPreferences: viewerUserPreferencesJson,
                date: date || todayDate,
                page: ConnectLoadable(() =>
                    import(
                        'MenstrualCycleTracking/components/MenstrualCycleTrackingDetailView/MenstrualCycleTrackingPage'
                    )
                ),
                route: route
            })
        );
    }
};

Window.App.PhasedTrainingPlanSetupPage = function(subType, step) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    if (!step || isNaN(step) || step > 7 || step < 1) {
        NavigationUtil.navigate(`training-plans/setup/${subType}/1`);
    } else {
        Window.App.displayPage(
            new ReactComponentPageView({
                displayName: viewerSocialProfileJson.displayName,
                viewerSocialProfile: viewerSocialProfileJson,
                viewerUserPreferences: viewerUserPreferencesJson,
                subType: subType,
                step: step,
                page: ConnectLoadable(() =>
                    import('PhasedTrainingPlan/components/PhasedTrainingPlanSetupPage/PhasedTrainingPlanSetupPage')
                )
            })
        );
    }
};

Window.App.PhasedTrainingPlanDashboard = function(planId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const openWorkoutModal = NavigationUtil.getQueryParameterByName('openWorkoutModal');

    if (openWorkoutModal) {
        NavigationUtil.clearQueryParameters();
    }

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            planId: planId,
            openWorkoutModal: openWorkoutModal,
            page: ConnectLoadable(() =>
                import('PhasedTrainingPlan/components/PhasedTrainingPlanDashboard/PhasedTrainingPlanDashboard')
            )
        })
    );
};

Window.App.SleepPage = async function(date, tab) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();

    const verifiedTab = tab && !isNaN(tab) && Object.values(TABS).indexOf(Number(tab)) !== -1 ? parseInt(tab) : null;

    const parsedDate = Date.parse(date);
    const verifiedDate = isNaN(date) && !isNaN(parsedDate) ? date : null;

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfile.toJSON(),
            viewerUserPreferences: viewerUserPreferences.toJSON(),
            viewerUserBiometrics: viewerUserBiometrics.toJSON(),
            page: ConnectLoadable(() => import('Sleep/SleepPageWrapper')),
            date: verifiedDate,
            tab: verifiedTab
        })
    );
};

Window.App.LatestPulseOxAcclimationPage = function() {
    const date = DateFormatter.formatISODate(new Date());
    Window.App.navigate('pulse-ox-acclimation/' + date, {
        trigger: true,
        replace: true
    });
};

Window.App.PulseOxAcclimationPage = function(date) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            profileImageUrlSmall: viewerSocialProfile.getProfileImageUrlSmall(),
            viewerUserPreferences: viewerUserPreferences.toJSON(),
            page: ConnectLoadable(() => import('PulseOxAcclimation/PulseOxAcclimationPage')),
            dateString: date
        })
    );
};

Window.App.PaceProGenesisPage = async function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('PacePro/containers/PaceProGenesisContainer')),
            isUserLoggedIn: viewerSocialProfile.isUserLoggedIn()
        })
    );
};

Window.App.PaceProCreatePage = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            profileImageUrlSmall: viewerSocialProfile.getProfileImageUrlSmall(),
            page: ConnectLoadable(() => import('PacePro/containers/PaceProCreateContainer'))
        })
    );
};

Window.App.PaceProEditPage = async function(id) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            profileImageUrlSmall: viewerSocialProfile.getProfileImageUrlSmall(),
            page: ConnectLoadable(() => import('PacePro/containers/PaceProEditContainer')),
            paceProId: id
        })
    );
};

Window.App.RespirationPage = async function(date, tab) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const respirationRole = await AsyncFeatureFlag.isFeatureEnabled(respirationRoleKey); // Clean up ticket: CA-185204

    const verifiedTab = tab && !isNaN(tab) && Object.values(TABS).indexOf(Number(tab)) !== -1 ? parseInt(tab) : null;

    const parsedDate = Date.parse(date);
    const verifiedDate = isNaN(date) && !isNaN(parsedDate) ? date : null;

    const todayDate = DateFormatter.formatISODate(new Date());
    const route = '/respiration/';
    const regex = new RegExp(REGEX_ISO_DATE_FORMAT);
    const isISODate = regex.test(date);

    if (!date || !isISODate || (isISODate && !moment(date).isValid())) {
        NavigationUtil.navigate(`${route}${todayDate}`, null, { replace: true });
        return;
    }

    Window.App.displayPage(
        respirationRole //Cleanup ticket: CA-185204
            ? new ReactComponentPageView({
                  viewerSocialProfile: viewerSocialProfileJson,
                  viewerUserPreferences: viewerUserPreferencesJson,
                  page: ConnectLoadable(() => import('Respiration/RespirationPageWrapper')),
                  date: verifiedDate,
                  tab: verifiedTab
              })
            : new ReactComponentPageView({
                  displayName: viewerSocialProfileJson.displayName,
                  viewerSocialProfile: viewerSocialProfileJson,
                  viewerUserPreferences: viewerUserPreferencesJson,
                  page: ConnectLoadable(() => import('Respiration/RespirationPageOld')),
                  date: date,
                  route
              })
    );
};

Window.App.PulseOxPage = function(date) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const todayDate = DateFormatter.formatISODate(new Date());
    const route = '/pulse-ox/';
    const regex = new RegExp(REGEX_ISO_DATE_FORMAT);
    const isISODate = regex.test(date);

    if (!date || !isISODate || (isISODate && !moment(date).isValid())) {
        NavigationUtil.navigate(`${route}${todayDate}`, null, { replace: true });
        return;
    }

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            page: ConnectLoadable(() => import('PulseOx/PulseOxPage')),
            date: date,
            route
        })
    );
};

Window.App.PaceProTimePage = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            profileImageUrlSmall: viewerSocialProfile.getProfileImageUrlSmall(),
            page: ConnectLoadable(() => import('PacePro/containers/PaceProTimeContainer'))
        })
    );
};

Window.App.CoursesList = async function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const userPreferences = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerUserPreferences: userPreferences,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('Courses/CoursesList/CoursesList'))
        })
    );
};

Window.App.CourseDetails = async function(courseId, queryString) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const userPreferences = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerUserPreferences: userPreferences,
            viewerSocialProfile: viewerSocialProfileJson,
            courseId: courseId,
            queryString: queryString,
            page: ConnectLoadable(() => import('Courses/CourseDetails/CourseDetails'))
        })
    );
};

Window.App.CreateFromCourse = async function(courseId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    //this should behave like the duplicate course functionality
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            duplicateByCourseId: courseId,
            page: ConnectLoadable(() => import('Courses/CourseCreate/CourseCreate'))
        })
    );
};

Window.App.CreateFromActivity = async function(activityId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            activityId: activityId,
            page: ConnectLoadable(() => import('Courses/CourseCreate/CourseCreate'))
        })
    );
};

Window.App.CourseCreate = async function(redirectUrl) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            redirectUrl: redirectUrl,
            page: ConnectLoadable(() => import('Courses/CourseCreate/CourseCreate'))
        })
    );
};

Window.App.CourseEdit = async function(courseId, redirectUrl, queryString) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            redirectUrl: redirectUrl,
            courseId: courseId,
            queryString: queryString,
            page: ConnectLoadable(() => import('Courses/CourseCreate/CourseCreate'))
        })
    );
};

Window.App.courseEmbed = async function(courseId) {
    const _this = this;

    Window.App.displayEmbed(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('Courses/CourseEmbedMap/CourseEmbed')),
            userPreferences: viewerUserPreferences,
            pageClassName: 'content',
            entityId: courseId,
            viewerIsAuthenticated: _this.viewerIsAuthenticated,
            urlBustValue: _this.urlBustValue,
            contextPath: NavigationUtil.url('')
        })
    );
};

Window.App.PowerGuidancePage = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('PowerGuidance/Components/PowerGuidancePage/PowerGuidancePage'))
        })
    );

    // displayPage() clears all modals, perform this step after so the GDPR modal does not get removed
    GdprUploadUtil.handleNoConsentGranted(true);
};

Window.App.PowerGuidanceCreateStrategy = function(courseId, powerGuidanceId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            powerGuidanceId: powerGuidanceId,
            courseId: courseId,
            page: ConnectLoadable(() =>
                import('PowerGuidance/Components/PowerGuidanceCreateStrategy/PowerGuidanceCreateStrategy')
            )
        })
    );

    // displayPage() clears all modals, perform this step after so the GDPR modal does not get removed
    GdprUploadUtil.handleNoConsentGranted(true);
};

Window.App.PowerGuidanceSelectCourse = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() =>
                import('PowerGuidance/Components/PowerGuidanceSelectCourse/PowerGuidanceSelectCourse')
            )
        })
    );

    // displayPage() clears all modals, perform this step after so the GDPR modal does not get removed
    GdprUploadUtil.handleNoConsentGranted(true);
};

Window.App.PowerGuidanceSelectCourseOrEvent = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() =>
                import('PowerGuidance/Components/PowerGuidancePage/PowerGuidanceSelectCourseOrEvent')
            )
        })
    );

    // displayPage() clears all modals, perform this step after so the GDPR modal does not get removed
    GdprUploadUtil.handleNoConsentGranted(true);
};

Window.App.PowerGuidanceEventsList = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('PowerGuidance/Components/PowerGuidancePage/PowerGuidanceEventsList'))
        })
    );

    // displayPage() clears all modals, perform this step after so the GDPR modal does not get removed
    GdprUploadUtil.handleNoConsentGranted(true);
};

Window.App.PowerGuidanceSetupPage = function(courseId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            courseId: courseId,
            page: ConnectLoadable(() =>
                import('PowerGuidance/Components/PowerGuidanceSetupPage/PowerGuidanceSetupPage')
            )
        })
    );

    // displayPage() clears all modals, perform this step after so the GDPR modal does not get removed
    GdprUploadUtil.handleNoConsentGranted(true);
};

Window.App.WeightPage = function(date, tab) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const route = '/weight/';
    let todayDate = DateFormatter.formatISODate(new Date());
    let verifiedTab =
        tab && !isNaN(tab) && Object.values(TABS).indexOf(Number(tab)) !== -1 ? Number(tab) : TABS.SEVEN_DAYS;

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            date: date || todayDate,
            tab: verifiedTab,
            page: ConnectLoadable(() => import('Weight/WeightPage')),
            route: route
        })
    );
};

Window.App.BPPage = function(date, tab) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const viewerUserBiometricsJson = viewerUserBiometrics.toJSON();
    const route = `${BPPageRoute}/`;
    let todayDate = DateFormatter.formatISODate(new Date());
    let verifiedTab =
        tab && !isNaN(tab) && Object.values(TABS).indexOf(Number(tab)) !== -1 ? Number(tab) : TABS.ONE_DAY;

    // if we don't have a date or a tab the page is first loaded
    if (!tab && !date) {
        AnalyticsUtil.push({
            event: 'track_event',
            category: 'Blood Pressure Page Events',
            action: 'Open Blood Pressure Page'
        });
    }

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerUserBiometrics: viewerUserBiometricsJson,
            date: date || todayDate,
            tab: verifiedTab,
            page: ConnectLoadable(() => import('BloodPressure/BPPage')),
            route: route
        })
    );
};

Window.App.ActivitiesComparePage = async function(activity1, activity2, activity3, activity4) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('Activity/ActivitiesCompare/ActivitiesCompareContainer')),
            activitiesIDs: [activity1, activity2, activity3, activity4].filter((id) => id)
        })
    );
};

Window.App.PregnancyTrackingPage = function(skipGetStarted) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const route = '/pregnancy-tracking/';

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            skipGetStarted: skipGetStarted,
            page: ConnectLoadable(() => import('Pregnancy/PregnancyTrackingInitialSetup/PregnancyTrackingPage')),
            route: route
        })
    );
};

Window.App.PregnancyStopTrackingPage = (page, flow, params) => {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const route = '/pregnancy-stop-tracking/';
    let showLink = false;
    if (params) {
        const urlParser = new UrlParser();
        const queryObject = urlParser.parseQueryString(params);
        if (queryObject['showLink']) {
            showLink = !!queryObject['showLink'];
        }
    }
    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            pageNumber: page,
            flow: flow,
            page: ConnectLoadable(() => import('Pregnancy/PregnancyStopTracking/PregnancyStopTrackingPage')),
            showLink: showLink ? true : false,
            route: route
        })
    );
};

Window.App.PregnancyDetailPage = function(date) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON(),
        todayDate = DateFormatter.formatISODate(new Date());
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const route = '/pregnancy-detail/';

    // Disallow future dates in the URL
    if (date > todayDate) {
        NavigationUtil.navigate(route + todayDate);
    } else {
        Window.App.displayPage(
            new ReactComponentPageView({
                displayName: viewerSocialProfileJson.displayName,
                viewerSocialProfile: viewerSocialProfileJson,
                viewerUserPreferences: viewerUserPreferencesJson,
                date: date || todayDate,
                page: ConnectLoadable(() => import('Pregnancy/PregnancyDetailView/PregnancyDetailView')),
                route: route
            })
        );
    }
};

Window.App.GolfCourseStatsPage = function(queryString) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const urlParser = new UrlParser();
    const queryObject = urlParser.parseQueryString(queryString);
    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            profileImageUrlSmall: viewerSocialProfile.getProfileImageUrlSmall(),
            page: ConnectLoadable(() => import('GolfCourseStats/GolfCourseStatsPage')),
            queryString: queryString,
            courseSnapshotId: queryObject.courseSnapshotId,
            courseGlobalId: queryObject.courseGlobalId,
            backNineCourseGlobalId: queryObject.backNineCourseGlobalId,
            userPreferences: viewerUserPreferences
        })
    );
};

Window.App.GolfCourseScorecardsPage = function(queryString) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const urlParser = new UrlParser();
    const queryObject = urlParser.parseQueryString(queryString);
    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            profileImageUrlSmall: viewerSocialProfile.getProfileImageUrlSmall(),
            page: ConnectLoadable(() => import('GolfCourseStats/GolfCourseScorecardsPage')),
            queryString: queryString,
            courseSnapshotId: queryObject.courseSnapshotId,
            courseGlobalId: queryObject.courseGlobalId,
            backNineCourseGlobalId: queryObject.backNineCourseGlobalId,
            userPreferences: viewerUserPreferences
        })
    );
};

Window.App.FitnessAgePage = function(date) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const route = '/fitness-age/';

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            page: ConnectLoadable(() => import('FitnessAge/FitnessAgePage')),
            route
        })
    );
};

Window.App.BadgeChallengePage = async function(challengeId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('Challenges/BadgeChallengeFeature')),
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerSocialProfile: viewerSocialProfileJson,
            challengeId
        })
    );
};

Window.App.SharedEventPage = function(shareableEventUuid) {
    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('Calendar/SharedEventPage')),
            shareableEventUuid,
            isUserLoggedIn: viewerSocialProfile.isUserLoggedIn()
        })
    );
};

Window.App.GolfScorecardsPage = function(displayName) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerDisplayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            displayName: displayName,
            profileImageUrlSmall: viewerSocialProfile.getProfileImageUrlSmall(),
            page: ConnectLoadable(() => import('GolfScorecard/containers/GolfScorecardListPageContainer')),
            userPreferences: viewerUserPreferences
        })
    );
};

Window.App.GolfScorecardPage = function(scorecardId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerDisplayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            displayName: viewerSocialProfileJson.displayName,
            scorecardId: parseInt(scorecardId, 10),
            locale: viewerUserPreferences.getLocale(),
            profileImageUrlSmall: viewerSocialProfile.getProfileImageUrlSmall(),
            page: ConnectLoadable(() => import('GolfScorecard/containers/GolfScorecardPageContainer')),
            userPreferences: viewerUserPreferences
        })
    );
};

Window.App.HealthSnapshotListPage = function(date, snapshotId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('HealthSnapshot/HealthSnapshotListPage'))
        })
    );
};

Window.App.HealthSnapshotDetailsPage = function(date, snapshotId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('HealthSnapshot/HealthSnapshotDetailsPage')),
            date,
            snapshotId
        })
    );
};

Window.App.GoalsPage = function(param) {
    let goalId = parseInt(param, 10);
    let tab = Number.isInteger(goalId) ? null : param ? param.toUpperCase() : null;
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            tab: tab,
            goalId: goalId,
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('Goals/GoalsPageContainer')),
            isUserLoggedIn: viewerSocialProfile.isUserLoggedIn()
        })
    );
};

Window.App.PersonalRecordsPage = (displayName) => {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const route = '/personal-records/';

    const viewerIsOwner = displayName ? viewerUserPreferences.get('displayName') === displayName : true;

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerIsOwner: viewerIsOwner,
            page: ConnectLoadable(() => import('PersonalRecords/PersonalRecordsPageView')),
            route: route
        })
    );
};

Window.App.ECGRecordingListPage = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('ECG/ECGRecordingListPage'))
        })
    );
};

Window.App.ECGRecordingDetailsPage = function(ecgId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const viewerUserBiometricsJson = viewerUserBiometrics.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserBiometrics: viewerUserBiometricsJson,
            page: ConnectLoadable(() => import('ECG/ECGRecordingDetailsPage')),
            ecgId
        })
    );
};

Window.App.TrainingReadinessPage = (date) => {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            date: date,
            page: ConnectLoadable(() => import('TrainingReadiness/TrainingReadinessPage'))
        })
    );
};

Window.App.InsightsComparePage = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            page: ConnectLoadable(() => import('Insights/InsightsComparePage'))
        })
    );
};

Window.App.ConnectionsPage = (displayName) => {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerIsOwner = displayName ? viewerUserPreferences.get('displayName') === displayName : true;
    const route = '/connections/connections';

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: displayName || viewerSocialProfileJson.displayName,
            viewerDisplayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            viewerIsOwner,
            page: ConnectLoadable(() => import('Connections/ConnectionsPage')),
            route
        })
    );
};

Window.App.ConnectionsGoogleTab = () => {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const route = '/connections/google';

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerDisplayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            activeTab: 'google',
            viewerIsOwner: true,
            page: ConnectLoadable(() => import('Connections/ConnectionsPage')),
            route
        })
    );
};

Window.App.ConnectionsSearch = () => {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const route = '/connections/search';

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerDisplayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            activeTab: 'search',
            viewerIsOwner: true,
            page: ConnectLoadable(() => import('Connections/ConnectionsPage')),
            route
        })
    );
};

Window.App.MultiSportEditPage = async (activityId, parentActivityId) => {
    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('MultiSportEdit/MultiSportEditPage')),
            activityId,
            parentActivityId
        })
    );
};

Window.App.MultiSportTrimPage = async (activityId) => {
    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('MultiSportTrim/MultiSportTrimPage')),
            activityId
        })
    );
};

Window.App.GroupsListPage = (tab) => {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    let activeTab = GROUP_LIST_PAGE_TAB.myGroups;
    if (tab === 'search') {
        activeTab = GROUP_LIST_PAGE_TAB.findGroups;
    } else if (tab === 'create') {
        activeTab = GROUP_LIST_PAGE_TAB.createGroup;
    } else if (tab) {
        NavigationUtil.navigate('/groups', null, { trigger: false, replace: true });
    }

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            tab: activeTab,
            page: ConnectLoadable(() => import('Groups/GroupListPage'))
        })
    );
};

Window.App.GroupsListPageGroupLeft = () => {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('Groups/GroupListPage')),
            tab: GROUP_LIST_PAGE_TAB.myGroups,
            groupLeft: true
        })
    );
};

Window.App.GroupsSearchPage = (keyword) => {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            tab: GROUP_LIST_PAGE_TAB.findGroups,
            keyword: keyword || '',
            page: ConnectLoadable(() => import('Groups/GroupListPage'))
        })
    );
};

Window.App.GroupPage = function(groupId, defaultPageTab) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    // viewerUserBiometrics is required by Group Calendar
    const viewerUserBiometricsJson = viewerUserBiometrics.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerUserBiometrics: viewerUserBiometricsJson,
            groupId,
            defaultPageTab,
            page: ConnectLoadable(() => import('GroupPage/GroupPage'))
        })
    );
};

Window.App.GroupPageCreated = function(groupId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            groupId,
            groupCreated: true,
            page: ConnectLoadable(() => import('GroupPage/GroupPage'))
        })
    );
};

Window.App.GroupPageCheckAnnouncement = function(groupId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            groupId,
            checkAnnouncement: true,
            page: ConnectLoadable(() => import('GroupPage/GroupPage'))
        })
    );
};

Window.App.GroupPageErrorNotification = function(groupId, error) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    let errorType = null;
    let defaultPageTab;
    if (groupRequestErrors.hasOwnProperty(error)) {
        errorType = groupRequestErrors[error];
        if (
            errorType === groupRequestErrors.acceptRequestNoPermission ||
            errorType === groupRequestErrors.requestCancelled
        ) {
            defaultPageTab = TAB_IDS.GROUP_MEMBERS;
        }
    }

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            groupId,
            defaultPageTab,
            errorType,
            page: ConnectLoadable(() => import('GroupPage/GroupPage'))
        })
    );
};

Window.App.GroupSinglePostPage = function(groupId, postUuid, commentUuid) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const commentUuidUpperCase = typeof commentUuid === 'string' ? commentUuid.toUpperCase() : null;

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            groupId,
            postUuid,
            commentUuid: commentUuidUpperCase,
            page: ConnectLoadable(() => import('GroupPage/GroupSingleFeedPage/GroupSingleFeedPage'))
        })
    );
};

Window.App.GroupSingleActivityPage = function(groupId, activityId, commentUuid) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const commentUuidUpperCase = typeof commentUuid === 'string' ? commentUuid.toUpperCase() : null;

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            groupId,
            activityId,
            commentUuid: commentUuidUpperCase,
            page: ConnectLoadable(() => import('GroupPage/GroupSingleFeedPage/GroupSingleFeedPage'))
        })
    );
};

Window.App.JetLagTripSetupPage = (step) => {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            step,
            page: ConnectLoadable(() => import('JetLag/JetLagTripSetupPageWrapper'))
        })
    );
};

Window.App.JetLagTripEditPage = (tripId, step) => {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            step,
            tripId,
            page: ConnectLoadable(() => import('JetLag/JetLagTripSetupPageWrapper'))
        })
    );
};

Window.App.JetLagManageTripsPage = (step) => {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            step,
            page: ConnectLoadable(() => import('JetLag/JetLagManageTripsWrapper'))
        })
    );
};

Window.App.JetLagTripStatusPage = (tripId, date) => {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            tripId,
            date,
            displayName: viewerSocialProfileJson.displayName,
            page: ConnectLoadable(() => import('JetLag/JetLagTripStatusPageWrapper'))
        })
    );
};

Window.App.GroupPageChallengeTab = function(groupId, challengeUuid) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    // viewerUserBiometrics is required by Group Calendar
    const viewerUserBiometricsJson = viewerUserBiometrics.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerUserBiometrics: viewerUserBiometricsJson,
            groupId,
            defaultPageTab: TAB_IDS.CHALLENGES,
            challengeUuid,
            page: ConnectLoadable(() => import('GroupPage/GroupPage'))
        })
    );
};

Window.App.GroupPageChallengeCreated = function(groupId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    // viewerUserBiometrics is required by Group Calendar
    const viewerUserBiometricsJson = viewerUserBiometrics.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerUserBiometrics: viewerUserBiometricsJson,
            groupId,
            defaultPageTab: TAB_IDS.CHALLENGES,
            challengeCreated: true,
            page: ConnectLoadable(() => import('GroupPage/GroupPage'))
        })
    );
};

Window.App.GearsTrackingPage = async function(tab) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            page: ConnectLoadable(() => import('Gears/GearsTrackingPage')),
            activeTab: tab ? tab : '',
            isEditMode: false
        })
    );
};

Window.App.EditGearTrackingPage = (uuid, options) => {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            page: ConnectLoadable(() => import('Gears/GearsTrackingPage')),
            uuid: uuid,
            isEditMode: true
        })
    );
};

Window.App.AddGearTrackingPage = (uuid, options) => {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            page: ConnectLoadable(() => import('Gears/GearsTrackingPage')),
            tab: 'Add'
        })
    );
};

Window.App.EventDashboard = (tab) => {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerSocialProfile: viewerSocialProfileJson,
            activeTab: tab,
            page: ConnectLoadable(() => import('EventBasedGoals/dashboard/EventDashboard'))
        })
    );
};

Window.App.EventDetailsPage = async function(tab, eventId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerSocialProfile: viewerSocialProfileJson,
            eventId: eventId,
            tab: tab,
            page: ConnectLoadable(() => import('EventBasedGoals/details/EventDetailsPageWrapper'))
        })
    );
};

Window.App.EventPrioritySelectionPage = async (eventId, shareableEventUuid) => {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    const isNewGoal = NavigationUtil.getQueryParameterByName('new');
    const fromEvents = NavigationUtil.getQueryParameterByName('fromEvents');

    if (shareableEventUuid && shareableEventUuid === 'null') {
        shareableEventUuid = null;
    }

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerSocialProfile: viewerSocialProfileJson,
            primary: true,
            fromGoalsPage: true,
            eventId: shareableEventUuid || eventId,
            shareableEventUuid: shareableEventUuid,
            shareable: !!shareableEventUuid,
            newlyAdded: isNewGoal === 'true',
            fromEvents: fromEvents,
            page: ConnectLoadable(() => import('EventBasedGoals/event/EventTrainingPriorityPage'))
        })
    );
};

Window.App.EventTrainingPlanPage = async (eventId) => {
    // Not wanting training plan integration to be viewable as part of DSW
    const enabled = false;

    // Cleanup ticket: CA-154387
    // const enabled = await AsyncFeatureFlag.isFeatureEnabled(trainingPlanIntegrationRoleKey);

    if (!enabled) {
        Window.App.displayPageNotFoundPage();
        return;
    }

    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            displayName: viewerSocialProfileJson.displayName,
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerSocialProfile: viewerSocialProfileJson,
            eventId: eventId,
            page: ConnectLoadable(() => import('EventBasedGoals/event/EventTrainingPlanPageWrapper'))
        })
    );
};

Window.App.challengesMainPage = async function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('ChallengePage/index'))
        })
    );
};

Window.App.availableChallengesPage = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('ChallengePage/index')),
            defaultRoute: 'available-badge-challenges'
        })
    );
};
Window.App.availableVirtualChallengesPage = async function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('ChallengePage/index')),
            defaultRoute: 'available-virtual-challenges'
        })
    );
};
Window.App.virtualChallengeDetailPage = async function(challengeUuid) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('ChallengePage/index')),
            defaultRoute: 'virtual-challenge',
            challengeUuid
        })
    );
};
Window.App.currentChallengesPage = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('ChallengePage/index')),
            defaultRoute: 'current-challenges'
        })
    );
};
Window.App.pastChallengesPage = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('ChallengePage/index')),
            defaultRoute: 'past-challenges'
        })
    );
};
Window.App.createChallengePage = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('CreateChallengePage/CreateChallengePageIndex'))
        })
    );
};
Window.App.AdHocChallengeDetailPage = async function(challengeUuid) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() =>
                import('ChallengeDetailPage/AdHocChallengeDetailPage/AdHocChallengeDetailPageIndex')
            ),
            challengeUuid
        })
    );
};
Window.App.GroupChallengeDetailPage = async function(challengeUuid) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() =>
                import('ChallengeDetailPage/GroupChallengeDetailPage/GroupChallengeDetailPageIndex')
            ),
            challengeUuid
        })
    );
};
Window.App.VivoKidAdHocChallengeDetailPage = async function(challengeUuid) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() =>
                import('ChallengeDetailPage/VivoKidChallengeDetailPage/VivoKidAdHocChallengeDetailPageIndex')
            ),
            challengeUuid
        })
    );
};
Window.App.VivoKidTeamChallengeDetailPage = async function(challengeUuid) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() =>
                import('ChallengeDetailPage/VivoKidChallengeDetailPage/VivoKidTeamChallengeDetailPageIndex')
            ),
            challengeUuid
        })
    );
};
Window.App.AutoChallengeDetailPage = async function(type = 'current') {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() =>
                import('ChallengeDetailPage/AutoChallengeDetailPage/AutoChallengeDetailPageIndex')
            ),
            type: type === 'previous' ? 'previous' : 'current'
        })
    );
};
Window.App.AutoChallengeDetailPagePrevious = async function() {
    Window.App.AutoChallengeDetailPage('previous');
};

Window.App.inboxPage = async function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            page: ConnectLoadable(() => import('InboxPage/index'))
        })
    );
};

Window.App.CreateEventPage = function(date, eventId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreference: viewerUserPreferencesJson,
            eventId: eventId,
            date: date,
            edit: false,
            page: ConnectLoadable(() => import('EventBasedGoals/event/EventPageContainer'))
        })
    );
};

Window.App.CopyEventForm = function(eventId) {
    Window.App.CreateEventPage(null, eventId);
};

Window.App.EditEventPage = function(eventId, shareableEventUuid) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreference: viewerUserPreferencesJson,
            date: null,
            eventId: eventId,
            edit: true,
            shareableEventUuid: shareableEventUuid === 'null' ? null : shareableEventUuid,
            page: ConnectLoadable(() => import('EventBasedGoals/event/EventPageContainer'))
        })
    );
};

Window.App.EventGoalsPage = function(eventId, shareableEventUuid, isNewlyAddedEvent) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const courseId = NavigationUtil.getQueryParameterByName('course');
    const paceBand = NavigationUtil.getQueryParameterByName('paceBand');
    const fromEvents = NavigationUtil.getQueryParameterByName('fromEvents');

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            shareable: shareableEventUuid !== 'null',
            shareableEventUuid: shareableEventUuid === 'null' ? null : shareableEventUuid,
            eventId: eventId,
            newlyAdded: isNewlyAddedEvent === 'true',
            courseId: courseId,
            paceBand: paceBand,
            fromEvents: fromEvents,
            page: ConnectLoadable(() => import('EventBasedGoals/event/EventGoalsPage'))
        })
    );
};

Window.App.BodyBatteryPage = function(date, tab) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    const verifiedTab = tab && !isNaN(tab) && Object.values(TABS).indexOf(Number(tab)) !== -1 ? parseInt(tab) : null;

    const parsedDate = Date.parse(date);
    const verifiedDate = isNaN(date) && !isNaN(parsedDate) ? date : null;

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            page: ConnectLoadable(() => import('BodyBattery/BodyBatteryPage')),
            date: verifiedDate,
            tab: verifiedTab
        })
    );
};

Window.App.SettingsPage = async function(tab, privacyZone) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();

    // New and shiny Settings page
    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('Settings/SettingsPageWrapper')),
            tab: tab,
            displayName: viewerSocialProfileJson.displayName,
            profileId: viewerSocialProfileJson.profileId,
            privacyZone: privacyZone,
            viewerSocialProfile: viewerSocialProfileJson
        })
    );
};

Window.App.EventMatching = async function() {
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerUserPreferences: viewerUserPreferencesJson,
            page: ConnectLoadable(() => import('EventBasedGoals/event/EventMatchingPage'))
        })
    );
};

// GC5 Homepage
Window.App.GC5Homepage = async function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const viewerUserBiometricsJson = viewerUserBiometrics.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserBiometrics: viewerUserBiometricsJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            page: ConnectLoadable(() => import('GC5Homepage/HomepageWrapper')),
            displayName: viewerSocialProfileJson.displayName
        })
    );
};

Window.App.GC5SetUpPage = async function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('GC5SetUpPage/GC5SetUpPageWrapper'))
        })
    );
};

Window.App.GC5EditSecondaryStats = async function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const redirectToSettings = NavigationUtil.getQueryParameterByName('settings');

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            page: ConnectLoadable(() => import('GC5Homepage/GC5SecondaryStatsSection/EditSecondaryStatsPageWrapper')),
            displayName: viewerSocialProfileJson.displayName,
            redirectToSettings: redirectToSettings === 'true'
        })
    );
};

Window.App.GC5EditSecondaryStatsOld = () => NavigationUtil.navigate('/home/edit-at-a-glance', null, { replace: true });

Window.App.GC5AllSecondaryStats = async function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            page: ConnectLoadable(() => import('GC5Homepage/GC5SecondaryStatsSection/AllSecondaryStatsPageWrapper')),
            displayName: viewerSocialProfileJson.displayName
        })
    );
};

Window.App.GC5AllSecondaryStatsOld = () => NavigationUtil.navigate('/home/at-a-glance', null, { replace: true });

Window.App.GarminCoachPlanCreate = async function(plantype, workoutPlanTypeId, formIndex) {
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('AdaptiveTrainingPlans/plansetup/Setup')),
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            plantype: plantype,
            workoutPlanId: workoutPlanTypeId,
            formIndex: formIndex,
            edit: false
        })
    );
};

Window.App.GarminCoachPlanEdit = async function(athletePlanId, plantype, workoutPlanTypeId, formIndex) {
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('AdaptiveTrainingPlans/plansetup/Setup')),
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            athletePlanId: athletePlanId,
            plantype: plantype,
            workoutPlanId: workoutPlanTypeId,
            formIndex: formIndex,
            edit: !!athletePlanId
        })
    );
};

Window.App[GarminTrailsPageHash] = async function() {
    if (isCDCUser()) {
        Window.App.displayPageNotFoundPage();
        return;
    }
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const userPreferences = viewerUserPreferences.toJSON();
    const isGarminTrailsEnabledByFeatureFlagRole = await AsyncFeatureFlag.isFeatureEnabledForMultipleRoles(
        garminTrailsRoleKey
    ); // Cleanup ticket: CA-159335

    if (isGarminTrailsEnabledByFeatureFlagRole) {
        Window.App.displayPage(
            new ReactComponentPageView({
                viewerUserPreferences: userPreferences,
                viewerSocialProfile: viewerSocialProfileJson,
                page: ConnectLoadable(() => import('ComCourses/GarminTrailsPage'))
            })
        );
    } else {
        Window.App.displayPageNotFoundPage();
    }
};

Window.App[GarminTrailDetailsHash] = async function(countryCode, provinceName, courseName, tripReportId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const userPreferences = viewerUserPreferences.toJSON();
    const isGarminTrailsEnabledByFeatureFlagRole = await AsyncFeatureFlag.isFeatureEnabledForMultipleRoles(
        garminTrailsRoleKey
    ); // Cleanup ticket: CA-159335
    const showTrailsPageForUnauthenticatedUserValue = await fetch(
        `/modern/preference/${showTrailsPageForUnauthenticatedUserKey}`
    )
        .then((response) => response.json())
        .then((data) => data); // Clean up ticket: CA-172718
    const isGarminTrailsEnableForUnauthenticatedUser = showTrailsPageForUnauthenticatedUserValue.value === 'true';

    // when the user is not logged in
    if (!userPreferences?.preferredLocale) {
        // The api doesn't currently accept the locale in the format provided by the cookie, so is set to EN
        // const GARMIN_USER_PREFS = 'GarminUserPrefs';
        // viewerUserPreferences.preferredLocale = getCookieValue(GARMIN_USER_PREFS);
        userPreferences.preferredLocale = 'EN';
    }

    if (isGarminTrailsEnabledByFeatureFlagRole || isGarminTrailsEnableForUnauthenticatedUser) {
        Window.App.displayPage(
            new ReactComponentPageView({
                viewerSocialProfile: viewerSocialProfileJson,
                viewerUserPreferences: userPreferences,
                countryCode,
                provinceName,
                courseName,
                tripReportId,
                page: ConnectLoadable(() => import('ComCourses/TrailDetailsPage'))
            })
        );
    } else {
        Window.App.displayPageNotFoundPage();
    }
};

Window.App.AdvancedReporting = async function(dashboardId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const userPreferences = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerUserPreferences: userPreferences,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('AdvancedReporting/ARLazyLoadGate')),
            dashboardId: dashboardId
        })
    );
};

Window.App.AdvancedReportingEdit = async function(dashboardId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const userPreferences = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerUserPreferences: userPreferences,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('AdvancedReporting/ARLazyLoadGate')),
            dashboardId: dashboardId,
            initialAdvancedReportingMode: 'EDIT'
        })
    );
};

Window.App.trailsEmbed = async function(countryCode, provinceName, courseName) {
    const garminTrailsFeatureFlagRole = await fetch(`/modern/preference/${garminTrailsRoleKey}`)
        .then((response) => response.json())
        .then((data) => data); // Cleanup ticket: CA-159335
    const isGarminTrailsEnabledByFeatureFlagRole = garminTrailsFeatureFlagRole.value.includes('ROLE_DI_TESTER'); // no userRoles for not logged in user

    if (isGarminTrailsEnabledByFeatureFlagRole) {
        Window.App.displayEmbed(
            new ReactComponentPageView({
                page: ConnectLoadable(() => import('ComCourses/TrailEmbed/TrailEmbedWrapper')),
                pageClassName: 'content',
                countryCode,
                provinceName,
                courseName
            })
        );
    } else {
        Window.App.displayPageNotFoundPage();
    }
};

Window.App.MyTrailReviews = async function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const userPreferences = viewerUserPreferences.toJSON();
    const isGarminTrailsEnabledByFeatureFlagRole = await AsyncFeatureFlag.isFeatureEnabledForMultipleRoles(
        garminTrailsRoleKey
    ); // Cleanup ticket: CA-159335

    if (isGarminTrailsEnabledByFeatureFlagRole) {
        Window.App.displayPage(
            new ReactComponentPageView({
                viewerSocialProfile: viewerSocialProfileJson,
                viewerUserPreferences: userPreferences,
                page: ConnectLoadable(() => import('ComCourses/MyTrailReviewsPage'))
            })
        );
    } else {
        Window.App.displayPageNotFoundPage();
    }
};

Window.App.RaceManagementPage = function(view, redID) {
    if (!isRaceManagementMode()) {
        Window.App.displayPageNotFoundPage();
        return;
    }

    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerSocialProfile: viewerSocialProfileJson,
            viewParam: view,
            redID: redID,
            page: ConnectLoadable(() => import('EventBasedGoals/event/RaceEventManagementLandingPage'))
        })
    );
};

Window.App.HeartRatePage = function(date, tab) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('HeartRate/HeartRatePage')),
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            date: date,
            tab: tab
        })
    );
};

Window.App.FloorsPage = function(date, tab) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('Floors/FloorsPage')),
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            date: date,
            tab: tab
        })
    );
};

Window.App.MovementPage = function(date, tab) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            page: ConnectLoadable(() => import('Movement/MovementPage')),
            date: date,
            tab: tab
        })
    );
};

Window.App.GC5CaloriesPage = async function(date, tab) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('GC5CaloriesPage/CaloriesPage')),
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            date: date,
            tab: tab
        })
    );
};

Window.App.WorkoutPage = async function(workoutId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const workoutType = NavigationUtil.getQueryParameterByName('workoutType');
    const useReactPage =
        workoutType === SportTypes.Swimming || (await AsyncFeatureFlag.isFeatureEnabled(reactWorkoutPageRoleKey)); // Clean up ticket: CA-167480

    // TODO: remove the Backbone page once the new React page is 100% complete, including MSN workouts
    if (useReactPage) {
        // React page
        Window.App.displayPage(
            new ReactComponentPageView({
                page: ConnectLoadable(() => import('WorkoutPage/WorkoutPage')),
                initialPageState: WorkoutPageState.Readonly,
                viewerSocialProfile: viewerSocialProfileJson,
                viewerUserPreferences: viewerUserPreferencesJson,
                workoutId: workoutId,
                initialWorkoutType: workoutType
            })
        );
    } else {
        // Backbone page
        import('pages/workouts/WorkoutSummaryPageView').then(({ default: WorkoutSummaryPageView }) => {
            var view = new WorkoutSummaryPageView({
                userPreferences: viewerUserPreferences,
                id: new Date().getTime(),
                workoutId: workoutId
            });

            Window.App.displayPage(view);
        });
    }
};

Window.App.WorkoutPageCreate = async function(workoutType) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const useReactPage =
        workoutType === SportTypes.Swimming || (await AsyncFeatureFlag.isFeatureEnabled(reactWorkoutPageRoleKey)); // Clean up ticket: CA-167480

    // TODO: remove the Backbone page once the new React page is 100% complete, including MSN workouts
    if (useReactPage) {
        // React page
        Window.App.displayPage(
            new ReactComponentPageView({
                page: ConnectLoadable(() => import('WorkoutPage/WorkoutPage')),
                initialPageState: WorkoutPageState.Create,
                viewerSocialProfile: viewerSocialProfileJson,
                viewerUserPreferences: viewerUserPreferencesJson,
                initialWorkoutType: workoutType
            })
        );
    } else {
        // Backbone page
        import('pages/workouts/WorkoutPageView').then(({ default: WorkoutPageView }) => {
            var view = new WorkoutPageView({
                userPreferences: viewerUserPreferences,
                id: new Date().getTime(),
                workoutType: workoutType
            });

            Window.App.displayPage(view);
        });
    }
};

Window.App.WorkoutPageEdit = async function(workoutId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const workoutType = NavigationUtil.getQueryParameterByName('workoutType');
    const useReactPage =
        workoutType === SportTypes.Swimming || (await AsyncFeatureFlag.isFeatureEnabled(reactWorkoutPageRoleKey)); // Clean up ticket: CA-167480

    // TODO: remove the Backbone page once the new React page is 100% complete, including MSN workouts
    if (useReactPage) {
        // React page
        Window.App.displayPage(
            new ReactComponentPageView({
                page: ConnectLoadable(() => import('WorkoutPage/WorkoutPage')),
                initialPageState: WorkoutPageState.Edit,
                viewerSocialProfile: viewerSocialProfileJson,
                viewerUserPreferences: viewerUserPreferencesJson,
                workoutId: workoutId,
                initialWorkoutType: workoutType
            })
        );
    } else {
        // Backbone page
        import('pages/workouts/WorkoutPageView').then(({ default: WorkoutPageView }) => {
            var view = new WorkoutPageView({
                userPreferences: viewerUserPreferences,
                id: new Date().getTime(),
                workoutId: workoutId
            });
            Window.App.displayPage(view);
        });
    }
};

Window.App.ProviderWorkoutPage = async function(workoutSourceId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const useReactPage = await AsyncFeatureFlag.isFeatureEnabled(reactWorkoutPageRoleKey); // Clean up ticket: CA-167480

    // TODO: remove the Backbone page once the new React page is 100% complete, including MSN workouts
    if (useReactPage) {
        // React page
        Window.App.displayPage(
            new ReactComponentPageView({
                page: ConnectLoadable(() => import('WorkoutPage/WorkoutPage')),
                initialPageState: WorkoutPageState.Readonly,
                viewerSocialProfile: viewerSocialProfileJson,
                viewerUserPreferences: viewerUserPreferencesJson,
                workoutSourceId
            })
        );
    } else {
        // Backbone page
        import('pages/workouts/WorkoutSummaryPageView').then(({ default: WorkoutSummaryPageView }) => {
            var view = new WorkoutSummaryPageView({
                userPreferences: viewerUserPreferences,
                id: new Date().getTime(),
                workoutSourceId: workoutSourceId
            });

            Window.App.displayPage(view);
        });
    }
};

Window.App.ViewOnlyWorkoutPage = async function(trainingPlanId, workoutUuid) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const workoutType = NavigationUtil.getQueryParameterByName('workoutType');

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('WorkoutPage/WorkoutPage')),
            initialPageState: WorkoutPageState.Viewonly,
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            workoutUuid: workoutUuid,
            initialWorkoutType: workoutType,
            showTE: true,
            customHeader: {
                namespace: 'dsw',
                content: 'garmin_coach_label',
                url: `/training/adaptive/details/${trainingPlanId}?workoutId=${workoutUuid}`
            }
        })
    );
};

Window.App.HRVStatusPage = function(date, tab) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    const parsedDate = Date.parse(date);
    const verifiedDate = isNaN(date) && !isNaN(parsedDate) ? date : null;

    const verifiedTab = tab && !isNaN(tab) && Object.values(TABS).indexOf(Number(tab)) !== -1 ? parseInt(tab) : null;

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('HRVStatus/HRVStatusPageWrapper')),
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            date: verifiedDate,
            tab: verifiedTab
        })
    );
};

Window.App.HydrationPage = function(date, tab) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('Hydration/HydrationPage')),
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            date,
            tab
        })
    );
};

Window.App.StressPage = function(date, tab) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    const verifiedTab = tab && !isNaN(tab) && Object.values(TABS).indexOf(Number(tab)) !== -1 ? parseInt(tab) : null;

    const parsedDate = Date.parse(date);
    const verifiedDate = isNaN(date) && !isNaN(parsedDate) ? date : null;

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            page: ConnectLoadable(() => import('Stress/StressPage')),
            date: verifiedDate,
            tab: verifiedTab
        })
    );
};

Window.App.TrainingSetup = async function(type, pageIndex, step) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const userPreferences = viewerUserPreferences.toJSON();
    const dswEnabled = await AsyncFeatureFlag.isFeatureEnabled(dswRoleKey); // Clean up ticket: CA-161362
    const selectedPlan = NavigationUtil.getQueryParameterByName('selectedPlan') || null;

    if (!dswEnabled) {
        Window.App.displayPageNotFoundPage();
        return;
    }
    let pageStep = step === null || isNaN(step) ? null : parseInt(step);
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: userPreferences,
            fullLocale: viewerUserPreferences.getFullLocale(),
            type,
            pageIndex,
            step: pageStep,
            selectedPlan: selectedPlan,
            edit: false,
            page: ConnectLoadable(() => import('DSW/TrainingSetupWrapper'))
        })
    );
};

Window.App.AdaptiveCoachingTrainingPlanDetails = async function(trainingPlanId) {
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const dswEnabled = await AsyncFeatureFlag.isFeatureEnabled(dswRoleKey); // Clean up ticket: CA-161362

    if (!dswEnabled) {
        // Clean up ticket: CA-161362
        Window.App.displayPageNotFoundPage();
        return;
    }

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('DSW/details/AdaptiveCoachingTrainingPlanDetailsWrapper')),
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            trainingPlanId: trainingPlanId
        })
    );
};

Window.App.StrengthTrainingPlanDetails = async function(trainingPlanId) {
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const dswEnabled = await AsyncFeatureFlag.isFeatureEnabled(dswRoleKey); // Clean up ticket: CA-161362

    if (!dswEnabled) {
        // Clean up ticket: CA-161362
        Window.App.displayPageNotFoundPage();
        return;
    }

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('DSW/details/StrengthTrainingPlanDetailsWrapper')),
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            trainingPlanId: trainingPlanId
        })
    );
};

Window.App.AdaptiveCoachingEditGoalPage = async function(trainingPlanId) {
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const dswEnabled = await AsyncFeatureFlag.isFeatureEnabled(dswRoleKey); // Clean up ticket: CA-161362

    if (!dswEnabled) {
        // Clean up ticket: CA-161362
        Window.App.displayPageNotFoundPage();
        return;
    }

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('DSW/edit/goals/GoalsEditPageWrapper')),
            viewerUserPreferences: viewerUserPreferencesJson,
            trainingPlanId: trainingPlanId
        })
    );
};

Window.App.AdaptiveCoachingEditWorkoutPage = async function(trainingPlanId, section) {
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const dswEnabled = await AsyncFeatureFlag.isFeatureEnabled(dswRoleKey); // Clean up ticket: CA-161362

    if (!dswEnabled) {
        // Clean up ticket: CA-161362
        Window.App.displayPageNotFoundPage();
        return;
    }

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('DSW/edit/workout/WorkoutEditPageWrapper')),
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerSocialProfile: viewerSocialProfileJson,
            trainingPlanId: trainingPlanId,
            section: section
        })
    );
};

Window.App.AdaptiveCoachingEditTargetPage = async function(trainingPlanId, section) {
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const dswEnabled = await AsyncFeatureFlag.isFeatureEnabled(dswRoleKey); // Clean up ticket: CA-161362

    if (!dswEnabled) {
        // Clean up ticket: CA-161362
        Window.App.displayPageNotFoundPage();
        return;
    }

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('DSW/edit/targets/TargetEditPageWrapper')),
            viewerUserPreferences: viewerUserPreferencesJson,
            viewerSocialProfile: viewerSocialProfileJson,
            trainingPlanId: trainingPlanId,
            section: section
        })
    );
};

Window.App.AdaptiveCoachingStrengthRecommendationPage = async function(trainingPlanId) {
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('DSW/edit/strength/StrengthRecommendationEditPageWrapper')),
            viewerUserPreferences: viewerUserPreferencesJson,
            trainingPlanId: trainingPlanId
        })
    );
};

Window.App.TrainingSetupReview = async function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const userPreferences = viewerUserPreferences.toJSON();
    const dswEnabled = await AsyncFeatureFlag.isFeatureEnabled(dswRoleKey); // Clean up ticket: CA-161362

    if (!dswEnabled) {
        Window.App.displayPageNotFoundPage();
        return;
    }

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: userPreferences,
            improveFitness: false,
            page: ConnectLoadable(() => import('DSW/setup/PlanReviewPageWrapper'))
        })
    );
};

Window.App.ImproveTrainingSetupReview = async function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const userPreferences = viewerUserPreferences.toJSON();
    const dswEnabled = await AsyncFeatureFlag.isFeatureEnabled(dswRoleKey); // Clean up ticket: CA-161362

    if (!dswEnabled) {
        Window.App.displayPageNotFoundPage();
        return;
    }

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: userPreferences,
            improveFitness: true,
            page: ConnectLoadable(() => import('DSW/setup/PlanReviewPageWrapper'))
        })
    );
};

Window.App.AtpItpTrainingSetupReview = async function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const userPreferences = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: userPreferences,
            improveFitness: false,
            atpItp: true,
            page: ConnectLoadable(() => import('DSW/setup/PlanReviewPageWrapper'))
        })
    );
};

Window.App.GC5IntensityMinutesPage = async function(date, tab) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('GC5IntensityMinutesPage/IntensityMinutesPage')),
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            date: date,
            tab: tab
        })
    );
};

Window.App.GolfCourseListPage = function(displayName) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerDisplayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            displayName: displayName,
            profileImageUrlSmall: viewerSocialProfile.getProfileImageUrlSmall(),
            page: ConnectLoadable(() => import('GolfCourseStats/containers/GolfCourseListPageContainer')),
            userPreferences: viewerUserPreferences
        })
    );
};

Window.App.GolfScorecardShotMap = async function(displayName, scorecardId, holeNumber) {
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    const viewerSocialProfileJson = viewerSocialProfile.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('ShotMap/containers/GolfShotMapPageContainer')),
            viewerUserPreferences: viewerUserPreferencesJson,
            scorecardId: scorecardId,
            holeNumber: holeNumber,
            locale: viewerUserPreferences.getLocale(),
            viewerDisplayName: viewerSocialProfileJson.displayName,
            displayName: displayName
        })
    );
};

Window.App.GolfCourseShotMap = async function(displayName, courseSnapshotId, holeNumber) {
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    const viewerSocialProfileJson = viewerSocialProfile.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('ShotMap/containers/GolfShotMapPageContainer')),
            viewerUserPreferences: viewerUserPreferencesJson,
            holeNumber: holeNumber,
            locale: viewerUserPreferences.getLocale(),
            viewerDisplayName: viewerSocialProfileJson.displayName,
            courseSnapshotId: courseSnapshotId,
            displayName: displayName
        })
    );
};

Window.App.GolfTournamentCreatePage = function(leaderboardId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerDisplayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            profileImageUrlSmall: viewerSocialProfile.getProfileImageUrlSmall(),
            page: ConnectLoadable(() => import('GolfLeaderboard/containers/GolfTournamentCreatePageContainer')),
            userPreferences: viewerUserPreferences,
            leaderboardId: leaderboardId,
            locale: viewerUserPreferences.getLocale()
        })
    );
};

Window.App.GolfLeaderboardListPage = function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerDisplayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('GolfLeaderboard/containers/GolfLeaderboardListPageContainer')),
            userPreferences: viewerUserPreferences,
            locale: viewerUserPreferences.getLocale()
        })
    );
};

Window.App.GolfLeaderboardPage = function(leaderboardId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerDisplayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('GolfLeaderboard/GolfLeaderboardPage')),
            userPreferences: viewerUserPreferences,
            leaderboardId: leaderboardId,
            locale: viewerUserPreferences.getLocale()
        })
    );
};

Window.App.myScorecards = function() {
    Window.App.navigate('scorecards/' + this.displayName, {
        trigger: true,
        replace: true
    });
};

Window.App.scorecard = function(displayName) {
    Window.App.navigate('scorecards/' + displayName, {
        trigger: true,
        replace: true
    });
};

Window.App.myGolfCourses = function() {
    Window.App.navigate('golf-courses', {
        trigger: true,
        replace: true
    });
};

Window.App.golfCourseDetail = function(displayName, courseId) {
    // Redirect to new Golf Course Page
    Window.App.navigate(`golf-course-stats?courseGlobalId=${courseId}`, {
        trigger: true,
        replace: true
    });
};

Window.App.golfCourseOldDetail = function(displayName, courseOldId) {
    // Redirect to new Golf Course Page
    Window.App.navigate(`golf-course-stats?courseSnapshotId=${courseOldId}`, {
        trigger: true,
        replace: true
    });
};

Window.App.golfCourseHoleMap = async function(displayName, scorecardId, holeNumber) {
    // redirect from legacy page to new page
    Window.App.navigate(`golf-shots/${displayName}/scorecard/${scorecardId}/hole/${holeNumber}`, {
        trigger: true,
        replace: true
    });
};

Window.App.golfCourseOldMap = async function(displayName, courseOldId, holeNumber) {
    // redirect from legacy page to new page
    Window.App.navigate(`golf-shots/${displayName}/golf-course/${courseOldId}/hole/${holeNumber}`, {
        trigger: true,
        replace: true
    });
};

Window.App.myGolfLeaderboards = function() {
    Window.App.navigate('golf-leaderboards', { trigger: true, replace: true });
};

Window.App.golfCourseStats = function(queryString) {
    // redirect from legacy page to new page
    Window.App.navigate(`golf-course-stats?${queryString}`, {
        trigger: true,
        replace: true
    });
};

Window.App.StrengthTrainingPlansPage = async function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();
    const STPEnabled = await AsyncFeatureFlag.isFeatureEnabled(strengthTrainingPlansRoleKey); // Cleanup ticket: CA-174612
    const STPPhase2Enabled = await AsyncFeatureFlag.isFeatureEnabled(STPPhase2RoleKey); // Cleanup ticket: CA-191070

    if (!STPEnabled) {
        Window.App.displayPageNotFoundPage();
        return;
    }

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('StrengthTrainingPlans/StrengthTrainingPlansPage')),
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            STPPhase2Enabled: STPPhase2Enabled
        })
    );
};

Window.App.SocialGroupRidePage = function(activityId, groupRideId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const viewerUserPreferencesJson = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('SocialGroupRide/SocialGroupRidePage')),
            viewerSocialProfile: viewerSocialProfileJson,
            viewerUserPreferences: viewerUserPreferencesJson,
            activityId: activityId,
            groupRideId: groupRideId
        })
    );
};

/*
    TODO:
    CA-181526:
    - Add redirects in fitness.js when the new Training Plans page is almost done
    - Send anyone with ROLE_MBTESTER to /training-plan-new
    - > can't do that beforehand since those accounts would be forced to the new page while it's under construction
    - With the redirect in place, we can toggle between new and old using the preference flag
    CA-181529:
    - Update /training-plan-new to /training-plan in routes.js
    - Update /explore/training-plan to /training-plan?tab=FindAPlan in ATPStateMessage.jsx
    - Delete the now unused Backbone routes from fitness.js
*/
Window.App.TrainingPlansPage = async function() {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const userPreferences = viewerUserPreferences.toJSON();
    const isNewTrainingPlansPageEnabled = await AsyncFeatureFlag.isFeatureEnabled(newTrainingPlansPageRoleKey); // Cleanup ticket: CA-181375

    if (isNewTrainingPlansPageEnabled || viewerSocialProfileJson?.userRoles?.includes('ROLE_MBTESTER')) {
        // Cleanup ticket: CA-181375
        Window.App.displayPage(
            new ReactComponentPageView({
                viewerUserPreferences: userPreferences,
                viewerSocialProfile: viewerSocialProfileJson,
                fullLocale: viewerUserPreferences.getFullLocale(),
                page: ConnectLoadable(() => import('TrainingPlans/TrainingPlansPage'))
            })
        );
    } else {
        Window.App.displayPageNotFoundPage();
    }
};

// TODO: Check for GC+ Subscription
Window.App.TrainingGuidancePage = async function(trainingPlanId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const userPreferences = viewerUserPreferences.toJSON();

    // Cleanup ticket: CA-181375
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerUserPreferences: userPreferences,
            viewerSocialProfile: viewerSocialProfileJson,
            fullLocale: viewerUserPreferences.getFullLocale(),
            trainingPlanId: trainingPlanId,
            page: ConnectLoadable(() => import('TrainingGuidance/TrainingGuidancePage'))
        })
    );
};

// TODO: Check for GC+ Subscription
Window.App.TrainingGuidanceMediaPage = async function(trainingPlanId, articleId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    const userPreferences = viewerUserPreferences.toJSON();

    Window.App.displayPage(
        new ReactComponentPageView({
            viewerUserPreferences: userPreferences,
            viewerSocialProfile: viewerSocialProfileJson,
            fullLocale: viewerUserPreferences.getFullLocale(),
            trainingPlanId,
            articleId,
            page: ConnectLoadable(() => import('TrainingGuidanceMedia/TrainingGuidanceMediaPage'))
        })
    );
};

Window.App.RouteToGCPPage = async function() {
    navigateToSubscriptionPage();
};

Window.App.RaceTimePredictionPage = async function(eventId) {
    const isRaceTimePredictionEnabled = await AsyncFeatureFlag.isFeatureEnabled(projectedRaceTimePredictionRoleKey); // Cleanup ticket: CA-192406

    if (isRaceTimePredictionEnabled) {
        Window.App.displayPage(
            new ReactComponentPageView({
                page: ConnectLoadable(() => import('RaceTimePrediction/RaceTimePredictionPage')),
                eventId
            })
        );
    } else {
        Window.App.displayPageNotFoundPage();
    }
};

Window.App.myFitnessPalReturn = function(queryString) {
    const urlParser = new UrlParser();
    const params = urlParser.parseQueryString(queryString);
    NavigationUtil.clearQueryParameters();

    Window.App.displayPage(
        new ReactComponentPageView({
            page: ConnectLoadable(() => import('MFP/MyFitnessPalReturnPage')),
            params: params
        })
    );
};

Window.App.ActivityDetailsPage = async function(activityId) {
    const viewerSocialProfileJson = viewerSocialProfile.toJSON();
    Window.App.displayPage(
        new ReactComponentPageView({
            viewerDisplayName: viewerSocialProfileJson.displayName,
            viewerSocialProfile: viewerSocialProfileJson,
            page: ConnectLoadable(() => import('ActivityDetails/ActivityDetailsPage')),
            userPreferences: viewerUserPreferences,
            locale: viewerUserPreferences.getLocale(),
            activityId: activityId
        })
    );
};

Window.App.route('gear', 'GearsTrackingPage');
Window.App.route('gear/add', 'AddGearTrackingPage'); // Rename to /gear/add after deleting backbone code cleanup ticket CA-123980
Window.App.route('gear/:uuid/edit', 'EditGearTrackingPage');
Window.App.route('admin/support-tool-mat', 'MATSupportPage');
Window.App.route('profile(/:displayName)', 'ProfilePage');
Window.App.route('newsfeed', 'NewsFeedPage', blockInAccountSharingReadonly());
Window.App.route('calendar(/:year/:month)', 'CalendarPage');
Window.App.route('event(/:eventId)', 'SharedEventPage');
Window.App.route('diving', 'DivingPage');
Window.App.route('progress-summary', 'ProgressSummaryPage');
Window.App.route('delegation-authorization', 'DelegationAuthorizationPage');
Window.App.route('popularity-heatmap', 'PopularityPage');
Window.App.route('find-workout', 'WorkoutsPage');
Window.App.route('benchmark-exercises', 'BenchmarkExercisesPage');
Window.App.route('benchmark-exercises/:exerciseKey', 'BenchmarkExerciseDetailsContainer');
Window.App.route('exercises(/:categoryKey/:exerciseKey)', 'ExercisesPage');
Window.App.route('badges(/:displayName)', 'BadgesPage', blockInAccountSharingReadonly());
Window.App.route('pulse-ox-acclimation(/)', 'LatestPulseOxAcclimationPage');
Window.App.route('pulse-ox-acclimation/:date', 'PulseOxAcclimationPage');
Window.App.route('segment/:uuid', 'SegmentsPage', blockInAccountSharingReadonly());
Window.App.route('segments', 'SegmentsMainPage', blockInAccountSharingReadonly());
Window.App.route(
    'segment/createFromActivity(/:activityId)',
    'CreateSegmentFromActivity',
    blockInAccountSharingReadonly()
);
Window.App.route('segment/editSegment(/:segmentUuid)', 'EditSegment', blockInAccountSharingReadonly());
Window.App.route('training-plans/plan(/:athletePlanId)', 'ATPDailyPage');
Window.App.route('training-plans/plan(/:athletePlanId)/article(/:articleId)', 'ATPArticlePage');
Window.App.route('training-plans/setup(/:subType)(/:step)', 'PhasedTrainingPlanSetupPage');
Window.App.route('training-plans/dashboard(/:planId)', 'PhasedTrainingPlanDashboard');
Window.App.route('daily-summary(/:date)(/:card)(/:oldRoute)', 'DailySummaryPage');
Window.App.route('manage-permissions', 'GoToUsersHomePage');
Window.App.route('menstrual-cycle-tracking(/:date)', 'MenstrualCycleTrackingPage');
Window.App.route('sleep(/:date)(/:tab)', 'SleepPage');
Window.App.route('pacepro/create', 'PaceProCreatePage');
Window.App.route('pacepro/pace', 'PaceProTimePage');
Window.App.route('pacepro', 'PaceProGenesisPage');
Window.App.route('pacepro/edit(/:id)', 'PaceProEditPage');
Window.App.route(`respiration(/:date)(/:tab)`, 'RespirationPage');
Window.App.route('pulse-ox(/:date)', 'PulseOxPage');
Window.App.route('weight(/:date)(/:tab)', 'WeightPage');
Window.App.route('blood-pressure(/:date)(/:tab)', 'BPPage');
Window.App.route('pregnancy-tracking(/:skipGetStarted)', 'PregnancyTrackingPage');
Window.App.route('pregnancy-stop-tracking(/:page)(/:flow)', 'PregnancyStopTrackingPage');
Window.App.route('activities/compare(/:activity1)(/:activity2)(/:activity3)(/:activity4)', 'ActivitiesComparePage');
Window.App.route('pregnancy-detail(/:date)', 'PregnancyDetailPage');
Window.App.route('fitness-age', 'FitnessAgePage');
Window.App.route('power-guidance', 'PowerGuidancePage');
Window.App.route('power-guidance/setup(/:courseId)', 'PowerGuidanceSetupPage');
Window.App.route('power-guidance/create-strategy(/:courseId)(/:powerGuidanceId)', 'PowerGuidanceCreateStrategy');
Window.App.route('power-guidance/select-course', 'PowerGuidanceSelectCourse');
Window.App.route('power-guidance/create', 'PowerGuidanceSelectCourseOrEvent');
Window.App.route('power-guidance/select-event', 'PowerGuidanceEventsList');
Window.App.route('badge-challenge(/:challengeId)', 'BadgeChallengePage');
Window.App.route('health-snapshots', 'HealthSnapshotListPage');
Window.App.route('goals(/:tab)', 'GoalsPage');
Window.App.route('goal(/:goalId)', 'GoalsPage');
Window.App.route('health-snapshot(/:date)(/:snapshotId)', 'HealthSnapshotDetailsPage');
Window.App.route('personal-records(/:displayName)', 'PersonalRecordsPage');
Window.App.route(ecgRoute, 'ECGRecordingListPage', blockInReadOnly());
Window.App.route(`${ecgRoute}/:ecgId`, 'ECGRecordingDetailsPage', blockInReadOnly());
Window.App.route('training-readiness(/:date)', 'TrainingReadinessPage');
Window.App.route('insights', 'InsightsComparePage');
Window.App.route('connections', 'ConnectionsPage', blockInAccountSharingReadonly());
Window.App.route('connections/connections(/:displayName)', 'ConnectionsPage', blockInAccountSharingReadonly());
Window.App.route('connections/google', 'ConnectionsGoogleTab', blockInAccountSharingReadonly());
Window.App.route('connections/search', 'ConnectionsSearch', blockInAccountSharingReadonly());

/** groups routes */
Window.App.route('groups(/:tab)', 'GroupsListPage', blockInAccountSharingReadonly());
Window.App.route('groups/groupLeft', 'GroupsListPageGroupLeft', blockInAccountSharingReadonly());
Window.App.route('groups/search(/:keyword)', 'GroupsSearchPage', blockInAccountSharingReadonly());
Window.App.route('group/:groupId(/:defaultPageTab)', 'GroupPage', blockInAccountSharingReadonly());
Window.App.route('group/:groupId/error/:errorType', 'GroupPageErrorNotification', blockInAccountSharingReadonly());
Window.App.route('group/:groupId/groupCreated', 'GroupPageCreated', blockInAccountSharingReadonly());
Window.App.route('group/:groupId/checkAnnouncement', 'GroupPageCheckAnnouncement', blockInAccountSharingReadonly());
Window.App.route('group/:groupId/challenges/:challengeUuid', 'GroupPageChallengeTab', blockInAccountSharingReadonly());
Window.App.route(
    'group/:groupId/challenges/challengeCreated',
    'GroupPageChallengeCreated',
    blockInAccountSharingReadonly()
);
Window.App.route('group/:groupId/newspost/:postUuid', 'GroupSinglePostPage', blockInAccountSharingReadonly());
Window.App.route(
    'group/:groupId/newspost/:postUuid/comment/:commentUuid',
    'GroupSinglePostPage',
    blockInAccountSharingReadonly()
);
Window.App.route('group/:groupId/activity/:activityId', 'GroupSingleActivityPage', blockInAccountSharingReadonly());
Window.App.route(
    'group/:groupId/activity/:activityId/comment/:commentUuid',
    'GroupSingleActivityPage',
    blockInAccountSharingReadonly()
);

// Golf

Window.App.route('scorecards/:displayName', 'GolfScorecardsPage', blockInAccountSharingReadonly());
Window.App.route('my-scorecards', 'myScorecards', blockInAccountSharingReadonly());
Window.App.route('scorecards', 'myScorecards', blockInAccountSharingReadonly());
Window.App.route('profile/:displayName/scorecards', 'scorecard', blockInAccountSharingReadonly());
Window.App.route('scorecard/:scorecardId', 'GolfScorecardPage', blockInAccountSharingReadonly());
Window.App.route(
    'golf-shots/:displayName/scorecard/:scorecardId/hole/:holeNumber',
    'GolfScorecardShotMap',
    blockInAccountSharingReadonly()
);
Window.App.route('golf-shots/:displayName/golf-course/:courseSnapshotId/hole/:holeNumber', 'GolfCourseShotMap');
Window.App.route('profile/:displayName/course-hole/course/oid/:courseOldId/hole/:holeNumber', 'golfCourseOldMap');
Window.App.route(
    'profile/:displayName/course-hole/scorecard/:scorecardId/hole/:holeNumber',
    'golfCourseHoleMap',
    blockInAccountSharingReadonly()
);
Window.App.route('golf-course-stats', 'GolfCourseStatsPage');
Window.App.route('golf-course-stats/scorecards', 'GolfCourseScorecardsPage', blockInAccountSharingReadonly());
Window.App.route('golf-courses', 'GolfCourseListPage');
Window.App.route('my-golf-courses', 'myGolfCourses');
Window.App.route('profile/:displayName/golf-courses', 'myGolfCourses');
Window.App.route('profile/:displayName/golf-courses/:courseId', 'golfCourseDetail');
Window.App.route('profile/:displayName/golf-courses/oid/:courseOldId', 'golfCourseOldDetail');
Window.App.route('golf-course-hole-stats', 'golfCourseStats');
Window.App.route('golf-stats', 'GolfStatsPage');
Window.App.route('golf-tournament/create', 'GolfTournamentCreatePage', blockInAccountSharingReadonly());
Window.App.route('golf-leaderboard/:leaderboardId/edit', 'GolfTournamentCreatePage', blockInAccountSharingReadonly());
Window.App.route('golf-leaderboards', 'GolfLeaderboardListPage', blockInAccountSharingReadonly());
Window.App.route('golf-leaderboard/:leaderboardId', 'GolfLeaderboardPage', blockInAccountSharingReadonly());
Window.App.route('my-golf-leaderboards', 'myGolfLeaderboards', blockInAccountSharingReadonly());

/** groups routes */

Window.App.route('multi-sport(/:activityId/:parentActivityId)', 'MultiSportEditPage');
Window.App.route('multi-sport-trim(/:activityId)', 'MultiSportTrimPage');
Window.App.route(`${jetLagRoute}/setup(/:step)`, 'JetLagTripSetupPage');
Window.App.route(`${jetLagRoute}/edit(/:tripId)(/:step)`, 'JetLagTripEditPage');
Window.App.route(`${jetLagRoute}/manage-trips`, 'JetLagManageTripsPage');
Window.App.route(`${jetLagRoute}/trip/:tripId(/:date)(/:tab)`, 'JetLagTripStatusPage');
Window.App.route('event-based-goals/secondary(/:eventId)(/:shareableEventUuid)', 'SecondaryEventSelectionPage');
Window.App.route('event-dashboard(/:tab)', 'EventDashboard');
Window.App.route('event/:tab/:eventId', 'EventDetailsPage');
Window.App.route(
    'event-based-goals/event-training-priority(/:eventId)(/:shareableEventUuid)',
    'EventPrioritySelectionPage'
);
Window.App.route('event-training-plan(/:eventId)', 'EventTrainingPlanPage');
/**
 * react challenge page
 */
Window.App.route('challenge', 'challengesMainPage', blockInAccountSharingReadonly());
Window.App.route('challenge/:challengeUuid', 'GroupChallengeDetailPage', blockInAccountSharingReadonly());
Window.App.route('challenge/available-badge-challenges', 'availableChallengesPage', blockInAccountSharingReadonly());
Window.App.route(
    'challenge/available-virtual-challenges',
    'availableVirtualChallengesPage',
    blockInAccountSharingReadonly()
);
Window.App.route(
    'challenge/virtual-challenge/:challengeUuid',
    'virtualChallengeDetailPage',
    blockInAccountSharingReadonly()
);
Window.App.route('challenge/current-challenges', 'currentChallengesPage', blockInAccountSharingReadonly());
Window.App.route('challenge/past-challenges', 'pastChallengesPage', blockInAccountSharingReadonly());
Window.App.route('challenge/create-challenge', 'createChallengePage', blockInAccountSharingReadonly());
Window.App.route(`adhoc-challenge/:challengeUuid`, 'AdHocChallengeDetailPage', blockInAccountSharingReadonly());
Window.App.route(
    `vivofitjr-challenge/:challengeUuid`,
    'VivoKidAdHocChallengeDetailPage',
    blockInAccountSharingReadonly()
);
Window.App.route(
    `vivofitjr-teamchallenge/:challengeUuid`,
    'VivoKidTeamChallengeDetailPage',
    blockInAccountSharingReadonly()
);
Window.App.route(`auto-challenge`, 'AutoChallengeDetailPage', blockInAccountSharingReadonly());
Window.App.route(`auto-challenge/previous`, 'AutoChallengeDetailPagePrevious', blockInAccountSharingReadonly());
/** end - react challenge page */
Window.App.route('calendar/event(/:date)', 'CreateEventPage', blockInAccountSharingReadonly());
Window.App.route(
    'calendar/event/edit(/:eventId)(/:shareableEventUuid)',
    'EditEventPage',
    blockInAccountSharingReadonly()
);
Window.App.route('calendar/event/copy/:eventId', 'CopyEventForm', blockInAccountSharingReadonly());
Window.App.route(
    'event-goals/:eventId/:shareableEventUuid/:isNewlyAddedEvent',
    'EventGoalsPage',
    blockInAccountSharingReadonly()
);
Window.App.route('import-data', 'importData');

Window.App.route(`${bodyBatteryRoute}(/:date)(/:tab)`, 'BodyBatteryPage');

Window.App.route('inbox', 'inboxPage', blockInAccountSharingReadonly());

Window.App.route('settings(/:tab)(/privacy-zone/:privacyZone)', 'SettingsPage');

Window.App.route('courses', 'CoursesList', blockInAccountSharingReadonly());
Window.App.route('course/:courseId', 'CourseDetails', blockInAccountSharingReadonly());
Window.App.route('course/create(?redirectUrl=*redirectUrl)', 'CourseCreate', blockInAccountSharingReadonly());
Window.App.route('course/edit/:courseId(?redirectUrl=*redirectUrl)', 'CourseEdit', blockInAccountSharingReadonly());
Window.App.route('course/create-from-activity/:activityId', 'CreateFromActivity', blockInAccountSharingReadonly());
Window.App.route('course/create-from-course/:courseId', 'CreateFromCourse', blockInAccountSharingReadonly());
Window.App.route('course/embed/:courseId', 'courseEmbed', blockInAccountSharingReadonly());

Window.App.route('event/matching', 'EventMatching', blockInAccountSharingReadonly());

Window.App.route(
    'training-plans/plan/setup/:plantype(/:workoutPlanTypeId)(/:formIndex)',
    'GarminCoachPlanCreate',
    blockInAccountSharingReadonly()
);
Window.App.route(
    'training-plans/plan/:athletePlanId/edit/:plantype(/:workoutPlanTypeId)(/:formIndex)',
    'GarminCoachPlanEdit',
    blockInAccountSharingReadonly()
);

// Advanced Reporting
Window.App.route('performance-dashboard(/:dashboardId)', 'AdvancedReporting');
Window.App.route('performance-dashboard(/:dashboardId)/edit', 'AdvancedReportingEdit');

Window.App.route(garminTrailsRoute, GarminTrailsPageHash, blockInAccountSharingReadonly());
Window.App.route(
    `${garminTrailsRoute}/:countryCode/:provinceName/:courseName(/:tripReportId)`,
    GarminTrailDetailsHash,
    blockInAccountSharingReadonly()
);
Window.App.route(
    `${garminTrailsRoute}/embed/:countryCode/:provinceName/:courseName`,
    'trailsEmbed',
    blockInAccountSharingReadonly()
);
Window.App.route(`${garminTrailsRoute}/${myTrailReviewsHash}`, 'MyTrailReviews', blockInAccountSharingReadonly());

Window.App.route('race-management/:view(/:redID)', 'RaceManagementPage');

// workout page(s)
Window.App.route('workout/:workoutId', 'WorkoutPage');
Window.App.route('workout/create/:workoutType', 'WorkoutPageCreate');
Window.App.route('workout/edit/:workoutId', 'WorkoutPageEdit');
Window.App.route('provider-workout/:workoutSourceId', 'ProviderWorkoutPage');
Window.App.route('workout/view/:trainingPlanId/:workoutUuid', 'ViewOnlyWorkoutPage');

// GC5 Pages
Window.App.route(`home`, 'GC5Homepage');
Window.App.route(`home/edit-at-a-glance`, 'GC5EditSecondaryStats');
Window.App.route(`home/at-a-glance`, 'GC5AllSecondaryStats');
Window.App.route(`home/editSecondaryStats`, 'GC5EditSecondaryStatsOld');
Window.App.route(`home/viewAllSecondaryStats`, 'GC5AllSecondaryStatsOld');
Window.App.route(`heart-rate(/:date)(/:tab)`, 'HeartRatePage');
Window.App.route(`floors(/:date)(/:tab)`, 'FloorsPage');
Window.App.route(`steps(/:date)(/:tab)`, 'MovementPage');
Window.App.route(`pushes(/:date)(/:tab)`, 'MovementPage');
Window.App.route(`calories(/:date)(/:tab)`, `GC5CaloriesPage`);
Window.App.route(`calories-in-out(/:date)(/:tab)`, `GC5CaloriesPage`);
Window.App.route(`hrv-status(/:date)(/:tab)`, 'HRVStatusPage');
Window.App.route(`hydration(/:date)(/:tab)`, 'HydrationPage');
Window.App.route(`stress(/:date)(/:tab)`, 'StressPage');
Window.App.route('intensity-minutes(/:date)(/:tab)', 'GC5IntensityMinutesPage');

Window.App.route(`training/setup(/:type)(/:pageIndex)(/:step)`, 'TrainingSetup');
Window.App.route('training/adaptive/details/:trainingPlanId', 'AdaptiveCoachingTrainingPlanDetails');
Window.App.route('training/strength/details/:trainingPlanId', 'StrengthTrainingPlanDetails');
Window.App.route('training/plan/setup-review', 'TrainingSetupReview');
Window.App.route('training/plan/improve-setup-review', 'ImproveTrainingSetupReview');
Window.App.route('training/plan/atp-itp-setup-review', 'AtpItpTrainingSetupReview');
Window.App.route('training/adaptive/details/edit/goal/:trainingPlanId', 'AdaptiveCoachingEditGoalPage');
Window.App.route('training/adaptive/details/edit/workout/:trainingPlanId/:section', 'AdaptiveCoachingEditWorkoutPage');
Window.App.route('training/adaptive/details/edit/target/:trainingPlanId/:section', 'AdaptiveCoachingEditTargetPage');
Window.App.route(
    'training/adaptive/details/edit/strength-recommendations/:trainingPlanId',
    'AdaptiveCoachingStrengthRecommendationPage'
);

Window.App.route(`home/setUp`, 'GC5SetUpPage');
Window.App.route(`stp/setUp`, 'StrengthTrainingPlansPage');

Window.App.route('group-ride(/:activityId/:groupRideId)', 'SocialGroupRidePage');

// Training Plans
Window.App.route('training-plan-new', 'TrainingPlansPage'); // TODO: Remove -new from the URL in CA-181529

// Training Guidance
Window.App.route('training-guidance(/:trainingPlanId)', 'TrainingGuidancePage');
Window.App.route('training-guidance/media(/:trainingPlanId)/article(/:articleId)', 'TrainingGuidanceMediaPage');

Window.App.route('race-time-prediction/:eventId', 'RaceTimePredictionPage'); // TODO: Add eventId

// Report
Window.App.route('report/:reportId/:reportType/:activeTab/:startDate/:untilDate', 'ReportsRangePage');
Window.App.route('report(/:reportId)(/:reportType)(/:activeTab)(/:vo2MaxToggle)', 'ReportsPage');

// GC+ Redirections
Window.App.route(subscription, 'RouteToGCPPage');
Window.App.route(`z8ben/${subscription}`, 'RouteToGCPPage');

// MFP
Window.App.route('mfp/*', 'myFitnessPalReturn');

// Activity Details
Window.App.route('activity-react/:activityId', 'ActivityDetailsPage');
